import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

// Utility functions
const roundCurrency = (value) => Math.round(value * 100) / 100;
const roundPercent = (value) => Math.round(value * 100) / 100;

// ============== COMPREHENSIVE HOME OFFICE & AUGUSTA RULE CALCULATOR ==============
export const HomeOfficeCalculator = ({ onSave, onClose }) => {
  const [method, setMethod] = useState("regular");
  const [formData, setFormData] = useState({
    // Square footage
    home_sqft: "",
    office_sqft: "",
    // Annual expenses
    annual_mortgage: "",
    annual_home_insurance: "",
    annual_property_taxes: "",
    annual_repairs: "",
    annual_cleaning: "",
    // Internet
    internet_annual: "",
    internet_business_percent: "70",
  });

  // Utilities - multiple items
  const [utilities, setUtilities] = useState([
    { name: "Electric", annualCost: "" },
    { name: "Gas", annualCost: "" },
    { name: "Water", annualCost: "" },
  ]);

  // Augusta Rule meetings
  const [augustaMeetings, setAugustaMeetings] = useState([]);
  const [newMeeting, setNewMeeting] = useState({ date: "", dailyRate: "", description: "" });

  const addUtility = () => {
    setUtilities([...utilities, { name: "", annualCost: "" }]);
  };

  const updateUtility = (index, field, value) => {
    const updated = [...utilities];
    updated[index] = { ...updated[index], [field]: value };
    setUtilities(updated);
  };

  const removeUtility = (index) => {
    setUtilities(utilities.filter((_, i) => i !== index));
  };

  const addMeeting = () => {
    if (newMeeting.dailyRate) {
      setAugustaMeetings([...augustaMeetings, { ...newMeeting }]);
      setNewMeeting({ date: "", dailyRate: "", description: "" });
    }
  };

  const removeMeeting = (index) => {
    setAugustaMeetings(augustaMeetings.filter((_, i) => i !== index));
  };

  // Calculate deductions
  const calculateDeductions = () => {
    const warnings = [];
    const homeSqft = parseFloat(formData.home_sqft) || 0;
    const officeSqft = parseFloat(formData.office_sqft) || 0;

    if (homeSqft <= 0 || officeSqft <= 0) {
      return { totalEstimatedDeduction: 0, warnings: ["Enter square footage to calculate"] };
    }

    if (officeSqft > homeSqft) {
      return { totalEstimatedDeduction: 0, warnings: ["Office cannot exceed home size"] };
    }

    const homeOfficePercent = officeSqft / homeSqft;

    // Utilities deduction
    const utilityDetails = utilities
      .filter(u => u.annualCost)
      .map(item => ({
        name: item.name || "Utility",
        annualCost: parseFloat(item.annualCost) || 0,
        deductibleAmount: roundCurrency((parseFloat(item.annualCost) || 0) * homeOfficePercent),
      }));

    const utilitiesDeduction = roundCurrency(
      utilityDetails.reduce((sum, item) => sum + item.deductibleAmount, 0)
    );

    // Internet deduction (separate business use %)
    const internetBusinessPercent = parseFloat(formData.internet_business_percent) || 0;
    const internetDeduction = roundCurrency(
      (parseFloat(formData.internet_annual) || 0) * (internetBusinessPercent / 100)
    );

    // Home office allocations
    const mortgageDeduction = roundCurrency((parseFloat(formData.annual_mortgage) || 0) * homeOfficePercent);
    const insuranceDeduction = roundCurrency((parseFloat(formData.annual_home_insurance) || 0) * homeOfficePercent);
    const propertyTaxDeduction = roundCurrency((parseFloat(formData.annual_property_taxes) || 0) * homeOfficePercent);
    const repairsDeduction = roundCurrency((parseFloat(formData.annual_repairs) || 0) * homeOfficePercent);
    const cleaningDeduction = roundCurrency((parseFloat(formData.annual_cleaning) || 0) * homeOfficePercent);

    // Augusta Rule (max 14 days)
    if (augustaMeetings.length > 14) {
      warnings.push("Augusta Rule allows up to 14 rental days/year. Review meeting count.");
    }

    const augustaRuleTotal = roundCurrency(
      augustaMeetings.reduce((sum, meeting) => sum + (parseFloat(meeting.dailyRate) || 0), 0)
    );

    // Warnings
    if (internetBusinessPercent > 100) {
      warnings.push("Internet business-use percent cannot exceed 100%.");
    }

    if (homeOfficePercent > 0.5) {
      warnings.push("Home office % is high (>50%). Ensure exclusive-use documentation.");
    }

    const totalEstimatedDeduction = roundCurrency(
      utilitiesDeduction +
      internetDeduction +
      mortgageDeduction +
      insuranceDeduction +
      propertyTaxDeduction +
      repairsDeduction +
      cleaningDeduction +
      augustaRuleTotal
    );

    return {
      homeOfficePercent: roundPercent(homeOfficePercent * 100),
      utilitiesDeduction,
      internetDeduction,
      mortgageDeduction,
      insuranceDeduction,
      propertyTaxDeduction,
      repairsDeduction,
      cleaningDeduction,
      augustaRuleTotal,
      totalEstimatedDeduction,
      utilityDetails,
      warnings,
    };
  };

  // Simplified method calculation
  const calculateSimplified = () => {
    const sqft = Math.min(parseFloat(formData.office_sqft) || 0, 300);
    return sqft * 5; // $5 per sq ft, max 300 sq ft = $1,500
  };

  const deductions = calculateDeductions();
  const simplifiedAmount = calculateSimplified();
  const regularAmount = deductions.totalEstimatedDeduction;
  const recommendedMethod = regularAmount > simplifiedAmount ? "regular" : "simplified";
  const finalAmount = method === "simplified" ? simplifiedAmount : regularAmount;

  const handleSave = () => {
    const description = method === "simplified"
      ? `Simplified method: ${formData.office_sqft} sq ft @ $5/sq ft`
      : `Regular method: ${deductions.homeOfficePercent}% home office + ${augustaMeetings.length} Augusta Rule days`;

    onSave({
      name: "Home Office & Augusta Rule Deduction",
      category: "Office & Workspace",
      description,
      estimated_amount: finalAmount,
      calculation_method: method,
      details: {
        ...formData,
        utilities,
        augustaMeetings,
        breakdown: deductions
      }
    });
  };

  return (
    <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-2">
      {/* IRS Info Banner */}
      <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
        <h4 className="font-semibold text-blue-800 mb-2">IRS Home Office + Augusta Rule</h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• <strong>Home Office:</strong> Space must be exclusive & regular business use</li>
          <li>• <strong>Simplified:</strong> $5/sq ft, max 300 sq ft = $1,500</li>
          <li>• <strong>Regular:</strong> Actual expenses × office %</li>
          <li>• <strong>Augusta Rule:</strong> Rent home to business for meetings, up to 14 days tax-free</li>
        </ul>
      </div>

      {/* Method Selection */}
      <Tabs value={method} onValueChange={setMethod}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="simplified">Simplified ($5/sq ft)</TabsTrigger>
          <TabsTrigger value="regular">Regular + Augusta Rule</TabsTrigger>
        </TabsList>

        {/* Simplified Method */}
        <TabsContent value="simplified" className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label>Dedicated Office Square Footage *</Label>
            <Input
              type="number"
              placeholder="e.g., 150"
              value={formData.office_sqft}
              onChange={(e) => setFormData({ ...formData, office_sqft: e.target.value })}
            />
            <p className="text-xs text-stone-500">Max 300 sq ft for simplified method</p>
          </div>

          <div className="p-4 rounded-lg bg-[#1e3a5f]/5 border border-[#1e3a5f]/20">
            <div className="flex justify-between items-center">
              <div>
                <p className="font-medium text-[#1e3a5f]">Simplified Deduction</p>
                <p className="text-sm text-[#1e3a5f]">
                  {Math.min(parseFloat(formData.office_sqft) || 0, 300)} sq ft × $5
                </p>
              </div>
              <p className="text-2xl font-bold text-[#1e3a5f]">${simplifiedAmount.toLocaleString()}</p>
            </div>
            {parseFloat(formData.office_sqft) > 300 && (
              <p className="text-xs text-amber-600 mt-2">* Capped at 300 sq ft ($1,500 max)</p>
            )}
          </div>
        </TabsContent>

        {/* Regular Method + Augusta Rule */}
        <TabsContent value="regular" className="space-y-4 mt-4">
          <Accordion type="multiple" defaultValue={["sqft", "expenses"]} className="w-full">
            {/* Square Footage */}
            <AccordionItem value="sqft">
              <AccordionTrigger className="text-sm font-semibold">
                📐 Square Footage
              </AccordionTrigger>
              <AccordionContent className="space-y-4 pt-2">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Total Home Sq Ft *</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 2000"
                      value={formData.home_sqft}
                      onChange={(e) => setFormData({ ...formData, home_sqft: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Office Sq Ft *</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 200"
                      value={formData.office_sqft}
                      onChange={(e) => setFormData({ ...formData, office_sqft: e.target.value })}
                    />
                  </div>
                </div>
                {formData.home_sqft && formData.office_sqft && (
                  <Badge className="bg-blue-100 text-blue-700">
                    Home Office: {deductions.homeOfficePercent}%
                  </Badge>
                )}
              </AccordionContent>
            </AccordionItem>

            {/* Home Expenses */}
            <AccordionItem value="expenses">
              <AccordionTrigger className="text-sm font-semibold">
                🏠 Annual Home Expenses
              </AccordionTrigger>
              <AccordionContent className="space-y-4 pt-2">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Mortgage/Rent (Annual)</Label>
                    <Input
                      type="number"
                      placeholder="0.00"
                      value={formData.annual_mortgage}
                      onChange={(e) => setFormData({ ...formData, annual_mortgage: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Home Insurance (Annual)</Label>
                    <Input
                      type="number"
                      placeholder="0.00"
                      value={formData.annual_home_insurance}
                      onChange={(e) => setFormData({ ...formData, annual_home_insurance: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Property Taxes (Annual)</Label>
                    <Input
                      type="number"
                      placeholder="0.00"
                      value={formData.annual_property_taxes}
                      onChange={(e) => setFormData({ ...formData, annual_property_taxes: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Repairs/Maintenance</Label>
                    <Input
                      type="number"
                      placeholder="0.00"
                      value={formData.annual_repairs}
                      onChange={(e) => setFormData({ ...formData, annual_repairs: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Cleaning (Annual)</Label>
                    <Input
                      type="number"
                      placeholder="0.00"
                      value={formData.annual_cleaning}
                      onChange={(e) => setFormData({ ...formData, annual_cleaning: e.target.value })}
                    />
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Utilities */}
            <AccordionItem value="utilities">
              <AccordionTrigger className="text-sm font-semibold">
                💡 Utilities ({utilities.length})
              </AccordionTrigger>
              <AccordionContent className="space-y-3 pt-2">
                {utilities.map((utility, index) => (
                  <div key={index} className="flex gap-2 items-end">
                    <div className="flex-1">
                      <Input
                        placeholder="Utility name"
                        value={utility.name}
                        onChange={(e) => updateUtility(index, "name", e.target.value)}
                      />
                    </div>
                    <div className="w-32">
                      <Input
                        type="number"
                        placeholder="Annual $"
                        value={utility.annualCost}
                        onChange={(e) => updateUtility(index, "annualCost", e.target.value)}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeUtility(index)}
                      className="text-red-500"
                    >
                      ✕
                    </Button>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={addUtility}>
                  + Add Utility
                </Button>
              </AccordionContent>
            </AccordionItem>

            {/* Internet */}
            <AccordionItem value="internet">
              <AccordionTrigger className="text-sm font-semibold">
                🌐 Internet
              </AccordionTrigger>
              <AccordionContent className="space-y-4 pt-2">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Annual Internet Cost</Label>
                    <Input
                      type="number"
                      placeholder="0.00"
                      value={formData.internet_annual}
                      onChange={(e) => setFormData({ ...formData, internet_annual: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Business Use %</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      placeholder="70"
                      value={formData.internet_business_percent}
                      onChange={(e) => setFormData({ ...formData, internet_business_percent: e.target.value })}
                    />
                  </div>
                </div>
                <p className="text-xs text-stone-500">
                  Internet is calculated by business use %, not home office %
                </p>
              </AccordionContent>
            </AccordionItem>

            {/* Augusta Rule */}
            <AccordionItem value="augusta">
              <AccordionTrigger className="text-sm font-semibold">
                🏛️ Augusta Rule ({augustaMeetings.length}/14 days)
              </AccordionTrigger>
              <AccordionContent className="space-y-4 pt-2">
                <div className="p-3 rounded-lg bg-purple-50 border border-purple-200">
                  <p className="text-sm text-purple-800">
                    <strong>Augusta Rule:</strong> Rent your home to your business for meetings up to 14 days/year. 
                    The rental income is tax-free to you, and a deduction for your business.
                  </p>
                </div>

                {/* Existing meetings */}
                {augustaMeetings.length > 0 && (
                  <div className="space-y-2">
                    {augustaMeetings.map((meeting, index) => (
                      <div key={index} className="flex justify-between items-center p-2 bg-stone-50 rounded">
                        <div>
                          <p className="font-medium text-sm">{meeting.description || `Meeting ${index + 1}`}</p>
                          <p className="text-xs text-stone-500">{meeting.date || "No date"}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-purple-600">${parseFloat(meeting.dailyRate).toLocaleString()}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeMeeting(index)}
                            className="text-red-500 h-6 w-6 p-0"
                          >
                            ✕
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add new meeting */}
                <div className="grid grid-cols-3 gap-2">
                  <Input
                    type="date"
                    value={newMeeting.date}
                    onChange={(e) => setNewMeeting({ ...newMeeting, date: e.target.value })}
                  />
                  <Input
                    type="number"
                    placeholder="Daily rate $"
                    value={newMeeting.dailyRate}
                    onChange={(e) => setNewMeeting({ ...newMeeting, dailyRate: e.target.value })}
                  />
                  <Input
                    placeholder="Description"
                    value={newMeeting.description}
                    onChange={(e) => setNewMeeting({ ...newMeeting, description: e.target.value })}
                  />
                </div>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm" 
                  onClick={addMeeting}
                  disabled={!newMeeting.dailyRate}
                >
                  + Add Meeting Day
                </Button>

                {augustaMeetings.length > 14 && (
                  <p className="text-xs text-amber-600">
                    ⚠️ Exceeds 14-day limit. Consult a tax professional.
                  </p>
                )}
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          {/* Breakdown Summary */}
          {deductions.totalEstimatedDeduction > 0 && (
            <div className="p-4 rounded-lg bg-stone-50 border border-stone-200 space-y-2">
              <h4 className="font-semibold text-stone-900">Deduction Breakdown</h4>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                {deductions.mortgageDeduction > 0 && (
                  <>
                    <span className="text-stone-600">Mortgage/Rent:</span>
                    <span className="text-right">${deductions.mortgageDeduction.toLocaleString()}</span>
                  </>
                )}
                {deductions.insuranceDeduction > 0 && (
                  <>
                    <span className="text-stone-600">Insurance:</span>
                    <span className="text-right">${deductions.insuranceDeduction.toLocaleString()}</span>
                  </>
                )}
                {deductions.propertyTaxDeduction > 0 && (
                  <>
                    <span className="text-stone-600">Property Tax:</span>
                    <span className="text-right">${deductions.propertyTaxDeduction.toLocaleString()}</span>
                  </>
                )}
                {deductions.utilitiesDeduction > 0 && (
                  <>
                    <span className="text-stone-600">Utilities:</span>
                    <span className="text-right">${deductions.utilitiesDeduction.toLocaleString()}</span>
                  </>
                )}
                {deductions.internetDeduction > 0 && (
                  <>
                    <span className="text-stone-600">Internet:</span>
                    <span className="text-right">${deductions.internetDeduction.toLocaleString()}</span>
                  </>
                )}
                {deductions.repairsDeduction > 0 && (
                  <>
                    <span className="text-stone-600">Repairs:</span>
                    <span className="text-right">${deductions.repairsDeduction.toLocaleString()}</span>
                  </>
                )}
                {deductions.cleaningDeduction > 0 && (
                  <>
                    <span className="text-stone-600">Cleaning:</span>
                    <span className="text-right">${deductions.cleaningDeduction.toLocaleString()}</span>
                  </>
                )}
                {deductions.augustaRuleTotal > 0 && (
                  <>
                    <span className="text-purple-600 font-medium">Augusta Rule:</span>
                    <span className="text-right text-purple-600 font-medium">${deductions.augustaRuleTotal.toLocaleString()}</span>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Warnings */}
          {deductions.warnings?.length > 0 && (
            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
              {deductions.warnings.map((warning, idx) => (
                <p key={idx} className="text-sm text-amber-800">⚠️ {warning}</p>
              ))}
            </div>
          )}

          {/* Total */}
          <div className="p-4 rounded-lg bg-[#1e3a5f]/5 border border-[#1e3a5f]/20">
            <div className="flex justify-between items-center">
              <div>
                <p className="font-medium text-[#1e3a5f]">Regular Method Total</p>
                <p className="text-sm text-[#1e3a5f]">{deductions.homeOfficePercent}% office + Augusta Rule</p>
              </div>
              <p className="text-2xl font-bold text-[#1e3a5f]">${regularAmount.toLocaleString()}</p>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Recommendation */}
      {(formData.office_sqft || formData.home_sqft) && regularAmount > 0 && (
        <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
          <p className="text-sm text-blue-800">
            <strong>Recommendation:</strong> The <strong>{recommendedMethod}</strong> method gives you a higher deduction
            (${Math.max(simplifiedAmount, regularAmount).toLocaleString()})
          </p>
        </div>
      )}

      <DialogFooter className="sticky bottom-0 bg-white pt-4 border-t">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleSave} 
          className="bg-[#1e3a5f] hover:bg-[#2d5a8e]" 
          disabled={finalAmount <= 0}
        >
          Add Deduction (${finalAmount.toLocaleString()})
        </Button>
      </DialogFooter>
    </div>
  );
};

// ============== VEHICLE/MILEAGE CALCULATOR ==============
export const VehicleMileageCalculator = ({ onSave, onClose }) => {
  const [method, setMethod] = useState("standard");
  const [formData, setFormData] = useState({
    business_miles: "",
    gas_fuel: "",
    insurance: "",
    repairs_maintenance: "",
    registration: "",
    lease_payments: "",
    depreciation: "",
    business_use_percent: "100"
  });

  const MILEAGE_RATE_2024 = 0.67;

  const calculateStandard = () => {
    return (parseFloat(formData.business_miles) || 0) * MILEAGE_RATE_2024;
  };

  const calculateActual = () => {
    const totalExpenses = 
      (parseFloat(formData.gas_fuel) || 0) +
      (parseFloat(formData.insurance) || 0) +
      (parseFloat(formData.repairs_maintenance) || 0) +
      (parseFloat(formData.registration) || 0) +
      (parseFloat(formData.lease_payments) || 0) +
      (parseFloat(formData.depreciation) || 0);
    
    const businessPercent = parseFloat(formData.business_use_percent) || 100;
    return totalExpenses * (businessPercent / 100);
  };

  const standardAmount = calculateStandard();
  const actualAmount = calculateActual();
  const finalAmount = method === "standard" ? standardAmount : actualAmount;

  const handleSave = () => {
    onSave({
      name: "Vehicle/Mileage Deduction",
      category: "Travel & Transportation",
      description: method === "standard" 
        ? `Standard mileage: ${formData.business_miles} miles @ $${MILEAGE_RATE_2024}/mile`
        : `Actual expenses: ${formData.business_use_percent}% business use`,
      estimated_amount: finalAmount,
      calculation_method: method,
      details: formData
    });
  };

  return (
    <div className="space-y-6">
      <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
        <h4 className="font-semibold text-blue-800 mb-2">IRS Vehicle Deduction Rules</h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• <strong>Standard mileage:</strong> 67¢/mile for 2024</li>
          <li>• <strong>Actual expenses:</strong> Track all costs × business use %</li>
          <li>• Keep a mileage log with date, destination, purpose, and miles</li>
          <li>• Commuting to regular workplace is NOT deductible</li>
        </ul>
      </div>

      <Tabs value={method} onValueChange={setMethod}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="standard">Standard Mileage</TabsTrigger>
          <TabsTrigger value="actual">Actual Expenses</TabsTrigger>
        </TabsList>

        <TabsContent value="standard" className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label>Total Business Miles Driven (Annual)</Label>
            <Input
              type="number"
              placeholder="e.g., 12000"
              value={formData.business_miles}
              onChange={(e) => setFormData({ ...formData, business_miles: e.target.value })}
            />
            <p className="text-xs text-stone-500">Excludes commuting - only business travel</p>
          </div>
          <div className="p-4 rounded-lg bg-[#1e3a5f]/5 border border-[#1e3a5f]/20">
            <div className="flex justify-between items-center">
              <div>
                <p className="font-medium text-[#1e3a5f]">Standard Mileage Deduction</p>
                <p className="text-sm text-[#1e3a5f]">
                  {(parseFloat(formData.business_miles) || 0).toLocaleString()} miles × $0.67
                </p>
              </div>
              <p className="text-2xl font-bold text-[#1e3a5f]">${standardAmount.toLocaleString()}</p>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="actual" className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label>Business Use Percentage</Label>
            <Input
              type="number"
              min="0"
              max="100"
              placeholder="100"
              value={formData.business_use_percent}
              onChange={(e) => setFormData({ ...formData, business_use_percent: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Gas/Fuel (Annual)</Label>
              <Input
                type="number"
                placeholder="0.00"
                value={formData.gas_fuel}
                onChange={(e) => setFormData({ ...formData, gas_fuel: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Insurance (Annual)</Label>
              <Input
                type="number"
                placeholder="0.00"
                value={formData.insurance}
                onChange={(e) => setFormData({ ...formData, insurance: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Repairs & Maintenance</Label>
              <Input
                type="number"
                placeholder="0.00"
                value={formData.repairs_maintenance}
                onChange={(e) => setFormData({ ...formData, repairs_maintenance: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Registration & Fees</Label>
              <Input
                type="number"
                placeholder="0.00"
                value={formData.registration}
                onChange={(e) => setFormData({ ...formData, registration: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Lease Payments</Label>
              <Input
                type="number"
                placeholder="0.00"
                value={formData.lease_payments}
                onChange={(e) => setFormData({ ...formData, lease_payments: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Depreciation</Label>
              <Input
                type="number"
                placeholder="0.00"
                value={formData.depreciation}
                onChange={(e) => setFormData({ ...formData, depreciation: e.target.value })}
              />
            </div>
          </div>
          <div className="p-4 rounded-lg bg-[#1e3a5f]/5 border border-[#1e3a5f]/20">
            <div className="flex justify-between items-center">
              <div>
                <p className="font-medium text-[#1e3a5f]">Actual Expenses Deduction</p>
                <p className="text-sm text-[#1e3a5f]">{formData.business_use_percent}% of total expenses</p>
              </div>
              <p className="text-2xl font-bold text-[#1e3a5f]">${actualAmount.toLocaleString()}</p>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} className="bg-[#1e3a5f] hover:bg-[#2d5a8e]">
          Add Deduction (${finalAmount.toLocaleString()})
        </Button>
      </DialogFooter>
    </div>
  );
};

// ============== HEALTH INSURANCE CALCULATOR ==============
export const HealthInsuranceCalculator = ({ onSave, onClose }) => {
  const [formData, setFormData] = useState({
    monthly_premium: "",
    dental_premium: "",
    vision_premium: "",
    long_term_care: "",
    covers_spouse: false,
    covers_dependents: false,
    dependent_count: "0",
    has_employer_plan_available: false
  });

  const calculateDeduction = () => {
    const health = (parseFloat(formData.monthly_premium) || 0) * 12;
    const dental = (parseFloat(formData.dental_premium) || 0) * 12;
    const vision = (parseFloat(formData.vision_premium) || 0) * 12;
    const ltc = parseFloat(formData.long_term_care) || 0;
    return health + dental + vision + ltc;
  };

  const totalDeduction = calculateDeduction();

  const handleSave = () => {
    if (formData.has_employer_plan_available) return;
    onSave({
      name: "Self-Employed Health Insurance",
      category: "Insurance",
      description: `Health insurance premiums for self and ${formData.covers_spouse ? "spouse, " : ""}${formData.covers_dependents ? `${formData.dependent_count} dependent(s)` : ""}`.trim().replace(/, $/, ""),
      estimated_amount: totalDeduction,
      details: formData
    });
  };

  return (
    <div className="space-y-6">
      <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
        <h4 className="font-semibold text-blue-800 mb-2">Self-Employed Health Insurance Deduction</h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• <strong>100% deductible</strong> above-the-line (reduces AGI)</li>
          <li>• Covers you, spouse, dependents, and children under 27</li>
          <li>• Limited to your net self-employment income</li>
          <li>• NOT available if you're eligible for employer-sponsored plan</li>
        </ul>
      </div>

      <div className="space-y-4">
        <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={formData.has_employer_plan_available}
              onChange={(e) => setFormData({ ...formData, has_employer_plan_available: e.target.checked })}
              className="w-4 h-4"
            />
            <Label className="text-amber-800">I (or my spouse) have access to an employer health plan</Label>
          </div>
          {formData.has_employer_plan_available && (
            <p className="text-sm text-amber-700 mt-2">
              ⚠️ You cannot take this deduction if you're eligible for employer coverage
            </p>
          )}
        </div>

        {!formData.has_employer_plan_available && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Monthly Health Premium</Label>
                <Input
                  type="number"
                  placeholder="0.00"
                  value={formData.monthly_premium}
                  onChange={(e) => setFormData({ ...formData, monthly_premium: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Monthly Dental Premium</Label>
                <Input
                  type="number"
                  placeholder="0.00"
                  value={formData.dental_premium}
                  onChange={(e) => setFormData({ ...formData, dental_premium: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Monthly Vision Premium</Label>
                <Input
                  type="number"
                  placeholder="0.00"
                  value={formData.vision_premium}
                  onChange={(e) => setFormData({ ...formData, vision_premium: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Long-Term Care (Annual)</Label>
                <Input
                  type="number"
                  placeholder="0.00"
                  value={formData.long_term_care}
                  onChange={(e) => setFormData({ ...formData, long_term_care: e.target.value })}
                />
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.covers_spouse}
                  onChange={(e) => setFormData({ ...formData, covers_spouse: e.target.checked })}
                  className="w-4 h-4"
                />
                <Label>Covers Spouse</Label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.covers_dependents}
                  onChange={(e) => setFormData({ ...formData, covers_dependents: e.target.checked })}
                  className="w-4 h-4"
                />
                <Label>Covers Dependents</Label>
              </div>
            </div>

            <div className="p-4 rounded-lg bg-[#1e3a5f]/5 border border-[#1e3a5f]/20">
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-medium text-[#1e3a5f]">Annual Deduction</p>
                  <p className="text-sm text-[#1e3a5f]">Form 1040 Line 17</p>
                </div>
                <p className="text-2xl font-bold text-[#1e3a5f]">${totalDeduction.toLocaleString()}</p>
              </div>
            </div>
          </>
        )}
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleSave} 
          className="bg-[#1e3a5f] hover:bg-[#2d5a8e]"
          disabled={formData.has_employer_plan_available || !formData.monthly_premium}
        >
          Add Deduction (${totalDeduction.toLocaleString()})
        </Button>
      </DialogFooter>
    </div>
  );
};

// ============== RETIREMENT CONTRIBUTIONS CALCULATOR ==============
export const RetirementCalculator = ({ onSave, onClose }) => {
  const [accountType, setAccountType] = useState("sep_ira");
  const [formData, setFormData] = useState({
    net_self_employment_income: "",
    age: "",
    solo_401k_employee: "",
    solo_401k_employer: ""
  });

  const netIncome = parseFloat(formData.net_self_employment_income) || 0;
  const age = parseInt(formData.age) || 30;
  const catchUp = age >= 50;

  const SEP_IRA_LIMIT = 69000;
  const SEP_IRA_PERCENT = 0.25;
  const SOLO_401K_EMPLOYEE_LIMIT = catchUp ? 30500 : 23000;
  const SOLO_401K_TOTAL_LIMIT = 69000;
  const SIMPLE_IRA_LIMIT = catchUp ? 19000 : 16000;

  const calculateSepIra = () => {
    const calculated = netIncome * SEP_IRA_PERCENT;
    return Math.min(calculated, SEP_IRA_LIMIT);
  };

  const calculateSolo401k = () => {
    const employeeContrib = Math.min(
      parseFloat(formData.solo_401k_employee) || 0,
      SOLO_401K_EMPLOYEE_LIMIT,
      netIncome
    );
    const maxEmployer = netIncome * 0.25;
    const employerContrib = Math.min(
      parseFloat(formData.solo_401k_employer) || maxEmployer,
      maxEmployer
    );
    return Math.min(employeeContrib + employerContrib, SOLO_401K_TOTAL_LIMIT);
  };

  const calculateSimpleIra = () => {
    return Math.min(netIncome, SIMPLE_IRA_LIMIT);
  };

  const getDeduction = () => {
    switch (accountType) {
      case "sep_ira": return calculateSepIra();
      case "solo_401k": return calculateSolo401k();
      case "simple_ira": return calculateSimpleIra();
      default: return 0;
    }
  };

  const deduction = getDeduction();

  const handleSave = () => {
    const typeNames = {
      sep_ira: "SEP-IRA",
      solo_401k: "Solo 401(k)",
      simple_ira: "SIMPLE IRA"
    };
    onSave({
      name: `${typeNames[accountType]} Contribution`,
      category: "Employee & Contractor Costs",
      description: `Self-employed retirement contribution (${typeNames[accountType]})`,
      estimated_amount: deduction,
      calculation_method: accountType,
      details: formData
    });
  };

  return (
    <div className="space-y-6">
      <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
        <h4 className="font-semibold text-blue-800 mb-2">Self-Employed Retirement Plans (2024)</h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• <strong>SEP-IRA:</strong> Up to 25% of net SE income, max $69,000</li>
          <li>• <strong>Solo 401(k):</strong> Employee ($23K) + Employer (25%), max $69,000</li>
          <li>• <strong>SIMPLE IRA:</strong> $16,000 ($19,000 if 50+)</li>
          <li>• Age 50+ can make catch-up contributions</li>
        </ul>
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Net Self-Employment Income</Label>
            <Input
              type="number"
              placeholder="0.00"
              value={formData.net_self_employment_income}
              onChange={(e) => setFormData({ ...formData, net_self_employment_income: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label>Your Age</Label>
            <Input
              type="number"
              placeholder="30"
              value={formData.age}
              onChange={(e) => setFormData({ ...formData, age: e.target.value })}
            />
            {catchUp && <p className="text-xs text-[#1e3a5f]">✓ Eligible for catch-up contributions</p>}
          </div>
        </div>

        <div className="space-y-2">
          <Label>Retirement Plan Type</Label>
          <RadioGroup value={accountType} onValueChange={setAccountType} className="grid grid-cols-3 gap-4">
            <div className="flex items-center space-x-2 p-3 rounded-lg border border-stone-200 hover:border-[#1e3a5f]/30">
              <RadioGroupItem value="sep_ira" id="sep_ira" />
              <Label htmlFor="sep_ira" className="cursor-pointer">SEP-IRA</Label>
            </div>
            <div className="flex items-center space-x-2 p-3 rounded-lg border border-stone-200 hover:border-[#1e3a5f]/30">
              <RadioGroupItem value="solo_401k" id="solo_401k" />
              <Label htmlFor="solo_401k" className="cursor-pointer">Solo 401(k)</Label>
            </div>
            <div className="flex items-center space-x-2 p-3 rounded-lg border border-stone-200 hover:border-[#1e3a5f]/30">
              <RadioGroupItem value="simple_ira" id="simple_ira" />
              <Label htmlFor="simple_ira" className="cursor-pointer">SIMPLE IRA</Label>
            </div>
          </RadioGroup>
        </div>

        {accountType === "solo_401k" && (
          <div className="grid grid-cols-2 gap-4 p-4 rounded-lg bg-stone-50">
            <div className="space-y-2">
              <Label>Employee Contribution</Label>
              <Input
                type="number"
                placeholder={`Max $${SOLO_401K_EMPLOYEE_LIMIT.toLocaleString()}`}
                value={formData.solo_401k_employee}
                onChange={(e) => setFormData({ ...formData, solo_401k_employee: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Employer Contribution</Label>
              <Input
                type="number"
                placeholder={`Max 25% = $${(netIncome * 0.25).toLocaleString()}`}
                value={formData.solo_401k_employer}
                onChange={(e) => setFormData({ ...formData, solo_401k_employer: e.target.value })}
              />
            </div>
          </div>
        )}

        <div className="p-4 rounded-lg bg-[#1e3a5f]/5 border border-[#1e3a5f]/20">
          <div className="flex justify-between items-center">
            <div>
              <p className="font-medium text-[#1e3a5f]">Maximum Deduction</p>
              <p className="text-sm text-[#1e3a5f]">
                {accountType === "sep_ira" && "25% of net income"}
                {accountType === "solo_401k" && "Employee + Employer contributions"}
                {accountType === "simple_ira" && `$${SIMPLE_IRA_LIMIT.toLocaleString()} limit`}
              </p>
            </div>
            <p className="text-2xl font-bold text-[#1e3a5f]">${deduction.toLocaleString()}</p>
          </div>
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleSave} 
          className="bg-[#1e3a5f] hover:bg-[#2d5a8e]"
          disabled={!formData.net_self_employment_income}
        >
          Add Deduction (${deduction.toLocaleString()})
        </Button>
      </DialogFooter>
    </div>
  );
};

// ============== CALCULATOR SELECTOR COMPONENT ==============
export const DeductionCalculators = ({ onAddDeduction }) => {
  const [activeCalculator, setActiveCalculator] = useState(null);

  const calculators = [
    { 
      id: "home_office", 
      name: "Home Office + Augusta", 
      icon: "🏠", 
      description: "Home office % + Augusta Rule meetings",
      color: "bg-blue-50 border-blue-200 hover:border-blue-400"
    },
    { 
      id: "vehicle", 
      name: "Vehicle/Mileage", 
      icon: "🚗", 
      description: "Standard mileage or actual expenses",
      color: "bg-purple-50 border-purple-200 hover:border-purple-400"
    },
    { 
      id: "health_insurance", 
      name: "Health Insurance", 
      icon: "🏥", 
      description: "Self-employed health premiums",
      color: "bg-[#1e3a5f]/5 border-[#1e3a5f]/20 hover:border-[#1e3a5f]"
    },
    { 
      id: "retirement", 
      name: "Retirement", 
      icon: "💰", 
      description: "SEP-IRA, Solo 401(k), SIMPLE IRA",
      color: "bg-amber-50 border-amber-200 hover:border-amber-400"
    }
  ];

  const handleSave = (deduction) => {
    onAddDeduction(deduction);
    setActiveCalculator(null);
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <svg className="w-5 h-5 text-[#1e3a5f]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 15.75V18m-7.5-6.75h.008v.008H8.25v-.008zm0 2.25h.008v.008H8.25V13.5zm0 2.25h.008v.008H8.25v-.008zm0 2.25h.008v.008H8.25V18zm2.498-6.75h.007v.008h-.007v-.008zm0 2.25h.007v.008h-.007V13.5zm0 2.25h.007v.008h-.007v-.008zm0 2.25h.007v.008h-.007V18zm2.504-6.75h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V13.5zm0 2.25h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V18zm2.498-6.75h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V13.5zM8.25 6h7.5v2.25h-7.5V6zM12 2.25c-1.892 0-3.758.11-5.593.322C5.307 2.7 4.5 3.65 4.5 4.757V19.5a2.25 2.25 0 002.25 2.25h10.5a2.25 2.25 0 002.25-2.25V4.757c0-1.108-.806-2.057-1.907-2.185A48.507 48.507 0 0012 2.25z" />
            </svg>
            Deduction Calculators
          </CardTitle>
          <CardDescription>
            Use guided wizards to calculate your deductions accurately
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {calculators.map((calc) => (
              <button
                key={calc.id}
                onClick={() => setActiveCalculator(calc.id)}
                className={`p-4 rounded-lg border-2 text-left transition-all ${calc.color}`}
                data-testid={`calculator-${calc.id}`}
              >
                <div className="text-2xl mb-2">{calc.icon}</div>
                <h4 className="font-semibold text-stone-900">{calc.name}</h4>
                <p className="text-xs text-stone-600 mt-1">{calc.description}</p>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Calculator Dialogs */}
      <Dialog open={activeCalculator === "home_office"} onOpenChange={() => setActiveCalculator(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>🏠 Home Office + Augusta Rule Calculator</DialogTitle>
            <DialogDescription>Calculate home office deduction with Augusta Rule meetings</DialogDescription>
          </DialogHeader>
          <HomeOfficeCalculator onSave={handleSave} onClose={() => setActiveCalculator(null)} />
        </DialogContent>
      </Dialog>

      <Dialog open={activeCalculator === "vehicle"} onOpenChange={() => setActiveCalculator(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>🚗 Vehicle/Mileage Deduction Calculator</DialogTitle>
            <DialogDescription>Calculate your vehicle deduction using standard mileage or actual expenses</DialogDescription>
          </DialogHeader>
          <VehicleMileageCalculator onSave={handleSave} onClose={() => setActiveCalculator(null)} />
        </DialogContent>
      </Dialog>

      <Dialog open={activeCalculator === "health_insurance"} onOpenChange={() => setActiveCalculator(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>🏥 Self-Employed Health Insurance Calculator</DialogTitle>
            <DialogDescription>Calculate your health insurance premium deduction</DialogDescription>
          </DialogHeader>
          <HealthInsuranceCalculator onSave={handleSave} onClose={() => setActiveCalculator(null)} />
        </DialogContent>
      </Dialog>

      <Dialog open={activeCalculator === "retirement"} onOpenChange={() => setActiveCalculator(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>💰 Retirement Contribution Calculator</DialogTitle>
            <DialogDescription>Calculate your maximum retirement contribution deduction</DialogDescription>
          </DialogHeader>
          <RetirementCalculator onSave={handleSave} onClose={() => setActiveCalculator(null)} />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DeductionCalculators;
